import { useEffect, useState } from "react";
import { isBefore, isAfter, addDays, subDays } from "date-fns";

import {
  Box,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Tooltip,
} from "@mui/material";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

import useLocalization from "../../hooks/useLocalization";
import useSKU from "../../hooks/useSKU";
import useSurvey from "../../hooks/useSurvey";

import ImageModal from "../modals/ImageModal";

import SurveyEntry_SectionQuestion from "./SurveyEntry_SectionQuestion";
import SkuStatus from "./SkuStatus";

const SurveyEntry_SkuMatrix = ({
  callResultId,
  section,
  questions,
  answers,
  call,
  location,
  onChange,
  isDisabled,
  testMode = false,
}) => {
  const { getMessage } = useLocalization();
  const { getSkus, skus, skuListings } = useSKU();
  const { getSkuStatus } = useSurvey();

  const [currentSku, setCurrentSku] = useState(null);
  const [selectedSkuId, setSelectedSkuId] = useState(null);
  const [status, setStatus] = useState();
  const [openImage, setOpenImage] = useState(false);
  const [showSkuList, setShowSkuList] = useState(false);
  const [skuStatuses, setSkuStatuses] = useState({});
  const [skuStatusesLoading, setSkuStatusesLoading] = useState(false);

  useEffect(() => {
    if (!section || !section.skuMatrix) return;

    getData();
  }, [section?.id, section?.skuMatrix, call?.id]);

  const normalizeSkuMatrix = (value) => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }

    return value;
  };

  const normalizeSkuIdsFromItems = (items) => {
    if (Array.isArray(items)) {
      return items
        .map((item) => {
          if (typeof item === "string") return item;
          if (!item || typeof item !== "object") return null;
          return item.skuId || item.id || null;
        })
        .filter(Boolean);
    }

    if (items && typeof items === "object") {
      return Object.values(items)
        .map((item) => {
          if (typeof item === "string") return item;
          if (!item || typeof item !== "object") return null;
          return item.skuId || item.id || null;
        })
        .filter(Boolean);
    }

    return [];
  };

  const isApplyListingsEnabled = (value) =>
    value === true || value === "true" || value === 1;

  const getData = async () => {
    console.log("getData.section: ", section);
    const skuMatrix = normalizeSkuMatrix(section?.skuMatrix);
    if (!skuMatrix) return;

    let skuIds = normalizeSkuIdsFromItems(skuMatrix.items);
    skuIds = [...new Set(skuIds)];

    if (skuIds.length === 0) {
      await getSkus({ id: { $in: [] } });
      return;
    }

    // Only filter by listings if applyListings is enabled
    if (call && isApplyListingsEnabled(skuMatrix.applyListings)) {
      const listedSkus = await skuListings(call.id);

      if (Array.isArray(listedSkus) && listedSkus.length > 0) {
        // listing responses can expose sku id as either skuId or id
        const listedSkuIds = listedSkus
          .map((sku) => sku?.skuId || sku?.id)
          .map((id) => String(id))
          .filter(Boolean);
        skuIds = skuIds.filter((skuId) => listedSkuIds.includes(String(skuId)));
      } else {
        // If no listings available, show no SKUs
        skuIds = [];
      }
    }

    // Fetch the SKUs - this will be empty if skuIds is empty
    await getSkus({ id: { $in: skuIds } });
  };

  useEffect(() => {
    if (!Array.isArray(skus) || skus.length === 0) {
      return;
    }

    // Keep current selection if SKU still exists after refresh.
    setCurrentSku((previousSku) => {
      const preferredId = selectedSkuId || previousSku?.id;
      if (!preferredId) return skus[0];

      const matchingSku = skus.find(
        (sku) => String(sku.id) === String(preferredId),
      );

      return matchingSku || skus[0];
    });
  }, [skus, selectedSkuId]);

  useEffect(() => {
    let cancelled = false;

    const loadStatuses = async () => {
      if (!showSkuList || !Array.isArray(skus) || skus.length === 0) {
        if (!showSkuList) setSkuStatusesLoading(false);
        return;
      }

      setSkuStatusesLoading(true);

      try {
        const rows = await Promise.all(
          skus.map(async (sku) => {
            const skuStatus = await getSkuStatus(
              section,
              questions,
              sku,
              answers,
              location,
            );
            return [String(sku.id), skuStatus];
          }),
        );

        if (cancelled) return;

        const statusMap = {};
        rows.forEach(([skuId, skuStatus]) => {
          statusMap[skuId] = skuStatus || { colorCode: "#ccc" };
        });
        setSkuStatuses(statusMap);
      } finally {
        if (!cancelled) setSkuStatusesLoading(false);
      }
    };

    loadStatuses();

    return () => {
      cancelled = true;
    };
  }, [showSkuList, skus, section, questions, answers, location]);

  const handlePrevSku = () => {
    setCurrentSku((current) => {
      let currentIndex = skus.findIndex((sku) => sku.id === current.id);
      if (currentIndex === 0) return current;
      const nextSku = skus[currentIndex - 1];
      setSelectedSkuId(nextSku?.id || null);
      return nextSku;
    });
  };

  const handleNextSku = () => {
    setCurrentSku((current) => {
      let currentIndex = skus.findIndex((sku) => sku.id === current.id);
      if (currentIndex === skus.length - 1) return current;
      const nextSku = skus[currentIndex + 1];
      setSelectedSkuId(nextSku?.id || null);
      return nextSku;
    });
  };

  const allowNext = () => {
    if (!Array.isArray(skus)) return false;

    let currentIndex = skus.findIndex((sku) => sku.id === currentSku.id);
    if (currentIndex === skus.length - 1) return false;
    return true;
  };

  const allowPrev = () => {
    if (!Array.isArray(skus)) return false;
    let currentIndex = skus.findIndex((sku) => sku.id === currentSku.id);
    if (currentIndex === 0) return false;
    return true;
  };

  const handleSelectSkuFromList = (sku) => {
    setCurrentSku(sku);
    setSelectedSkuId(sku?.id || null);
    setShowSkuList(false);
  };

  const getSkuStatusLabel = (skuStatus) => {
    if (!skuStatus) return "Not started";
    if (skuStatus.required && skuStatus.answered) return "Required complete";
    if (skuStatus.required && !skuStatus.answered) return "Required missing";
    if (!skuStatus.required && skuStatus.answered) return "Optional complete";
    return "Optional missing";
  };

  const showField = (name) => {
    if (!Array.isArray(section.skuMatrix.show.fields)) return false;
    const index = section.skuMatrix.show.fields.findIndex(
      (field) => field === name,
    );
    if (index === -1) return false;
    return true;
  };

  const showCustom = () => {
    if (!Array.isArray(section.skuMatrix.show.custom)) return false;
    if (section.skuMatrix.show.custom.length === 0) return false;
    return true;
  };

  return (
    <>
      <Box>
        {section?.text && (
          <Box sx={{ padding: "8px", paddingBottom: "4px" }}>
            <Typography sx={{ fontSize: "12px", whiteSpace: "pre-wrap" }}>
              {section.text}
            </Typography>
          </Box>
        )}
        <Box>
          {currentSku && Array.isArray(skus) && skus.length > 0 ? (
            <Box>
              <Box
                sx={{
                  paddingLeft: "4px",
                  backgroundColor: "#aaa",
                  color: "#000",
                  position: "sticky",
                  top: 60,
                  zIndex: 190,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    paddingRight: "4px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <ArrowCircleLeftIcon
                      onClick={handlePrevSku}
                      color="black"
                      sx={{ cursor: "pointer", opacity: allowPrev() ? 1 : 0.1 }}
                    />
                    <Box>
                      <Box sx={{ display: "flex" }}>
                        <SkuStatus
                          section={section}
                          questions={questions}
                          sku={currentSku}
                          answers={answers}
                          location={location}
                        />
                        <Typography
                          sx={{ fontSize: "10px", fontWeight: "bold" }}
                        >
                          {currentSku.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          paddingLeft: "8px",
                        }}
                        gap={1}
                      >
                        {showField("upc") && (
                          <Typography sx={{ fontSize: "8px" }}>
                            UPC: {currentSku.upc}
                          </Typography>
                        )}
                        {showField("clientCode") && (
                          <Typography sx={{ fontSize: "8px" }}>
                            Client Code: {currentSku.clientCode}
                          </Typography>
                        )}
                        {showField("packSize") && (
                          <Typography sx={{ fontSize: "8px" }}>
                            Pack: {currentSku.packSize}
                          </Typography>
                        )}
                      </Box>

                      {showCustom() && <Box></Box>}
                    </Box>
                    <Tooltip title="Open SKU navigator">
                      <IconButton
                        size="small"
                        onClick={() => setShowSkuList(true)}
                        sx={{ marginLeft: "6px" }}
                      >
                        <ListAltIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <ArrowCircleRightIcon
                    onClick={handleNextSku}
                    color="black"
                    sx={{ cursor: "pointer", opacity: allowNext() ? 1 : 0.1 }}
                  />
                </Box>
              </Box>
              {showField("image") && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "4px",
                    cursor: "pointer",
                  }}
                  onClick={() => setOpenImage(true)}
                >
                  <img
                    src={currentSku.image}
                    width="100px"
                    height="auto"
                  />
                </Box>
              )}
              <Box>
                <SurveyEntry_SectionQuestion
                  sku={currentSku}
                  callResultId={callResultId}
                  section={section}
                  questions={questions}
                  answers={answers}
                  call={call}
                  location={location}
                  onChange={onChange}
                  isDisabled={isDisabled}
                  testMode={testMode}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ padding: "8px" }}>
              <Typography
                textAlign="center"
                sx={{ fontSize: "12px", fontWeight: "bold" }}
              >
                {getMessage("noListingsInLocation")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {openImage && (
        <ImageModal
          title={currentSku.name}
          base64={currentSku.image}
          open={openImage}
          onClose={() => setOpenImage(false)}
        />
      )}

      <Dialog
        open={showSkuList}
        onClose={() => setShowSkuList(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: "8px",
          }}
        >
          Select SKU
          <IconButton
            aria-label="Close SKU list"
            size="small"
            onClick={() => setShowSkuList(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ paddingTop: 0 }}>
            {Array.isArray(skus) &&
              skus.map((sku) => {
                const skuStatus = skuStatuses[String(sku.id)] || {
                  colorCode: "#ccc",
                };
                const isActive = currentSku?.id === sku.id;

                return (
                  <ListItemButton
                    key={sku.id}
                    selected={isActive}
                    onClick={() => handleSelectSkuFromList(sku)}
                    sx={{ borderRadius: "6px", marginBottom: "4px" }}
                  >
                    <Box
                      sx={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: skuStatus.colorCode || "#ccc",
                        marginRight: "10px",
                        flexShrink: 0,
                      }}
                    />
                    <ListItemText
                      primary={sku.name}
                      secondary={sku.upc ? `UPC: ${sku.upc}` : null}
                    />
                    <Chip
                      size="small"
                      label={
                        skuStatusesLoading
                          ? "Checking..."
                          : getSkuStatusLabel(skuStatus)
                      }
                      sx={{ marginRight: "8px" }}
                    />
                    {isActive && (
                      <CheckCircleIcon
                        color="success"
                        fontSize="small"
                      />
                    )}
                  </ListItemButton>
                );
              })}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SurveyEntry_SkuMatrix;
